import CheckIcon from '@mui/icons-material/Check';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import DownloadIcon from '@mui/icons-material/FileDownload';
import UploadIcon from '@mui/icons-material/FileUpload';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '../../apiForm/panel/Paper';
import { downloadFile, readFile } from './downloadUtils';
import ProductsIcon from '../../icons/shelves.svg?react';
import ConfirmationDialog from '../../utilComponents/ConfirmationDialog';
import UploadDialog from './FileUpload/UploadDialog';
import useExportApi from './useExportApi';

export default function ProductsExport() {
  const { t } = useTranslation();
  const api = useExportApi();
  const { projectId } = useParams();

  const [downloading, setDownloading] = useState(false);
  const [downloadedProductsCount, setDownloadedProductsCount] = useState<
    number | undefined
  >();

  const [uploading, setUploading] = useState(false);
  const [uploadingDialog, setUploadingDialog] = useState(false);
  const [uploadedProductsCount, setUploadedProductsCount] = useState<
    number | undefined
  >();

  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleted, setDeleted] = useState(false);

  const handleProductsDownload = useCallback(async () => {
    setDownloading(true);
    const data = await api.getProducts(null);
    if (!data) {
      setDownloadedProductsCount(0);
      setDownloading(false);
      return;
    }
    setDownloadedProductsCount(data.split('\n').length - 1);
    downloadFile(
      data,
      `${projectId}-products-export.ndjson`,
      'application/x-ndjson',
    );
    setDownloading(false);
  }, [api, projectId]);

  const handleProductsUpload = async (file: File) => {
    setUploadingDialog(false);
    setUploading(true);
    const data = await readFile(file);
    if (!data) {
      setUploading(false);
      setUploadedProductsCount(0);
    }
    setUploadedProductsCount(data.split('\n').length - 1);
    api.uploadProducts(data);
    setUploading(false);
  };

  const handleProductDelete = useCallback(() => {
    setDeleteDialog(false);
    setDeleted(true);
    api.deleteProducts(null);
  }, [api]);

  return (
    <>
      <UploadDialog
        open={uploadingDialog}
        confirmationTitle={t('export.products.uploadConfirmation.title')}
        confirmationBody={t('export.products.uploadConfirmation.body')}
        onUpload={handleProductsUpload}
        onClose={() => {
          setUploadingDialog(false);
        }}
        metadataParser={async (file: File) => {
          const data = await readFile(file);
          return { Products: String(data.split('\n').length - 1) };
        }}
      />

      <ConfirmationDialog
        open={deleteDialog}
        title={t('export.products.deleteConfirmation.title')}
        body={t('export.products.deleteConfirmation.body')}
        onConfirm={handleProductDelete}
        onCancel={() => {
          setDeleteDialog(false);
        }}
      />

      <Paper
        legend={
          <Stack direction="row" gap={1} alignItems="center">
            <ProductsIcon style={{ fill: 'white' }} />
            <Typography variant="body2">
              {t('export.products.panelHeadline')}
            </Typography>
          </Stack>
        }
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-evenly"
        >
          <Stack direction="column" alignItems="flex-start" gap={2}>
            <Button
              onClick={handleProductsDownload}
              startIcon={<DownloadIcon />}
              variant="contained"
              loading={downloading}
              loadingPosition="start"
            >
              {t('export.products.downloadButton')}
            </Button>
            <Box sx={{ minHeight: '24px' }}>
              {downloadedProductsCount !== undefined && (
                <Stack direction="row" gap={1} alignItems="center">
                  <CheckIcon color="success" fontSize="small" />
                  <Typography variant="caption">
                    {t('export.products.downloadedHint', {
                      count: downloadedProductsCount,
                    })}
                  </Typography>
                </Stack>
              )}
            </Box>
          </Stack>

          <Stack direction="column" alignItems="flex-start" gap={2}>
            <Button
              onClick={() => {
                setUploadingDialog(true);
              }}
              startIcon={<UploadIcon />}
              variant="contained"
              loading={uploading}
              loadingPosition="start"
            >
              {t('export.products.uploadButton')}
            </Button>
            <Box sx={{ minHeight: '24px' }}>
              {uploadedProductsCount !== undefined && (
                <Stack direction="row" gap={1} alignItems="center">
                  <CheckIcon color="success" fontSize="small" />
                  <Typography variant="caption">
                    {t('export.products.uploadedHint', {
                      count: uploadedProductsCount,
                    })}
                  </Typography>
                </Stack>
              )}
            </Box>
          </Stack>

          <Stack direction="column" alignItems="flex-start" gap={2}>
            <Button
              onClick={() => {
                setDeleteDialog(true);
              }}
              startIcon={<DeleteForeverIcon />}
              variant="outlined"
              color="error"
            >
              {t('export.products.deleteButton')}
            </Button>
            <Box sx={{ minHeight: '24px' }}>
              {deleted && (
                <Stack direction="row" gap={1} alignItems="center">
                  <CheckIcon color="success" fontSize="small" />
                  <Typography variant="caption">
                    {t('export.products.deletedHint')}
                  </Typography>
                </Stack>
              )}
            </Box>
          </Stack>
        </Stack>
      </Paper>
    </>
  );
}
